import { usePathname, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import { useSessionStorage } from "usehooks-ts";

export type Path = {
  path: string | null;
  createdAt: string;
};
export const usePreviousPath = () => {
  const [prevPath, setPrevPath] = useSessionStorage<Path | null>(
    "prevPath",
    null
  );
  const [currentPath, setCurrentPath] = useSessionStorage<Path | null>(
    "currentPath",
    null
  );
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // Only add ? if there are search params
  const newFullUrl = `${pathname}${searchParams.toString() ? "?" + searchParams.toString() : ""}`;
  const isPathExpired = (timeString: string | undefined) => {
    const MAX_ALLOWED_DIFFERENCE = 10;

    if (!timeString) return true;

    const pathTime = new Date(timeString);

    const now = new Date();
    const difference =
      Math.abs(now.getTime() - pathTime.getTime()) / (1000 * 60);

    if (difference > MAX_ALLOWED_DIFFERENCE) {
      return true;
    }
    return false;
  };

  const storePathValues = () => {
    const authPages = [
      "/login",
      "/register",
      "/forgot-password",
      "/reset-password",
      "/verify",
      "/verify-email",
    ];

    // Checkout pages manage their own prevPath for auth redirect.
    // If storePathValues ran here it would overwrite what the checkout page
    // sets in its own useEffect (child effects fire before parent effects).
    const checkoutPages = ["/checkout-event", "/checkout", "/checkout-offer", "/checkout-group"];
    const isAuthPage = (path: string | null) => {
      if (!path) return false;
      // Use endsWith to match dynamic routes like /[course]/login
      return authPages.some((authPath) => path.endsWith(authPath));
    };
    const isCheckoutPage = (path: string | null) => {
      if (!path) return false;
      return checkoutPages.some((p) => path.includes(p));
    };

    // Only update prevPath if:
    // - the previous path (currentPath) is NOT an auth page (by pathname)
    // - the new path (pathname) is NOT an auth page (by pathname)
    // - the new path (pathname) is NOT a checkout page (those set prevPath themselves)
    if (
      currentPath?.path &&
      !isAuthPage(currentPath.path) &&
      !isAuthPage(pathname) &&
      !isCheckoutPage(pathname)
    ) {
      const prev = {
        path: currentPath.path,
        createdAt: new Date().toISOString(),
      };
      setPrevPath(prev);
    }

    // Always update currentPath to the new full url
    const current = {
      path: newFullUrl,
      createdAt: new Date().toISOString(),
    };
    setCurrentPath(current);
  };

  useEffect(() => {
    storePathValues();
  }, [newFullUrl]);

  return { prevPath, setPrevPath, isPathExpired };
};
